import { FiDollarSign, FiTarget } from "react-icons/fi";
import { useEffect, useMemo, useState } from "react";
import "./founderPlaybook.css";

/**
 * Founder Playbook — Mike's repeatable 10-step SaaS framework (Starter Story),
 * adapted to an AI clipping company (Clipbait) with the Lifetime-Deal motion
 * baked in. Mike avoids AI for ONE reason — platform/API risk — so step 1 is
 * about neutralizing exactly that.
 *
 * It's a living checklist: tick tasks off, watch the progress bar. State is
 * saved per-admin in localStorage.
 */

const STORAGE_KEY = "clipbait_founder_playbook_v1";

const PHASES = [
  {
    phase: "Phase 1 — Foundation (de-risk before you scale)",
    steps: [
      {
        id: "s1",
        title: "1. Proven idea + kill the AI platform risk",
        note: "AI clipping is already proven & profitable (Opus Clip, Klap, Vizard). Mike's only reason to avoid AI is depending on an API you don't control — so neutralize that, then the framework applies fully.",
        tasks: [
          ["s1a", "Confirm proven demand — competitors are profitable (✓ done)"],
          ["s1b", "Run 2+ transcription/model providers with automatic fallback (no single-vendor lock)"],
          ["s1c", "Own the render pipeline (Modal + FFmpeg) — every part is replaceable"],
          ["s1d", "Store source + rendered clips ourselves so we never re-depend on a 3rd party"],
          ["s1e", "Know our COGS cold (~$0.14/clip) so any discount/LTD stays profitable"],
        ],
      },
      {
        id: "s2",
        title: "2. Define a 'good-enough' MVP from competitors",
        note: "List what competitors' customers love most, build only that, ship fast, get people using it and telling you why it's crap.",
        tasks: [
          ["s2a", "Must-haves shipped: 9:16 + 16:9, AI captions, hook detection, watermark-free"],
          ["s2b", "Fast generate → preview → export loop working"],
          ["s2c", "Collect blunt user feedback (DMs, calls, a feedback widget)"],
        ],
      },
    ],
  },
  {
    phase: "Phase 2 — Monetize early with a Lifetime Deal",
    steps: [
      {
        id: "s3",
        title: "3. Offer a Lifetime Deal (LTD)",
        note: "Single payment for lifetime access — early cash + early believers. In Clipbait an LTD = a large NEVER-EXPIRING credit grant (we already support those) or a 'Lifetime' plan that grants X credits/month forever.",
        tasks: [
          ["s3a", "Decide the offer (e.g. $99 one-time = 1,000 never-expiring credits, or 'Founder Lifetime' = 300 credits/mo forever)"],
          ["s3b", "Wire fulfillment: admin grants never-expiring credits (Gift credits already exists) or add a 'lifetime' plan flag"],
          ["s3c", "Cap the number of LTD seats to create scarcity"],
        ],
      },
      {
        id: "s4",
        title: "4. Never give accounts away free — always charge",
        note: "If they pay, they use it and give real feedback. We already hard-gate generate behind a plan or credits.",
        tasks: [
          ["s4a", "Hard paywall on generate (plan OR credits) — ✓ done"],
          ["s4b", "Free tier = credits only + 1 brand; multi-brand requires subscription — ✓ done"],
        ],
      },
      {
        id: "s5",
        title: "5. Sell a PRIVATE LTD (target ≈ $30k)",
        note: "Go where clippers/streamers live. Mike raised ~$30k from Frill's private LTD before any marketplace.",
        tasks: [
          ["s5a", "List the communities: Reddit (r/NewTubers, r/Twitch, r/streaming, r/SocialMediaMarketing), clipper Discords, FB groups, X"],
          ["s5b", "Post/DM the private LTD offer with a clear seat cap + deadline"],
          ["s5c", "Track buyers + revenue toward the $30k goal"],
        ],
      },
    ],
  },
  {
    phase: "Phase 3 — Content & reach",
    steps: [
      {
        id: "s6",
        title: "6. Start writing content NOW (SEO + AI indexing)",
        note: "It's never too early. The longer it's up, the longer Google + ChatGPT index it and send you traffic. Use the LTD cash to fund it.",
        tasks: [
          ["s6a", "Landing pages + blog live"],
          ["s6b", "'Opus Clip alternative', 'Klap alternative', 'best AI clip generator' pages"],
          ["s6c", "Comparison / 'alternative-to' pages for every competitor"],
          ["s6d", "Great docs (AI needs good docs to recommend you)"],
        ],
      },
      {
        id: "s7",
        title: "7. Launch on AppSumo (target: close LTD at ≈ $100k)",
        note: "Huge reach + capital. Marketplace (lower fee) or work their Select sales team. They mail a massive list.",
        tasks: [
          ["s7a", "Apply to AppSumo (marketplace and/or Select)"],
          ["s7b", "Prep assets: demo video, copy, LTD tiers, support plan"],
          ["s7c", "Launch + ride their mailout; track toward $100k in the bank"],
        ],
      },
      {
        id: "s8",
        title: "8. One last private LTD (scarcity close)",
        note: "People hate missing out. Raise the price a bit, 'last ever' offer to your mailing list, then close the LTD forever.",
        tasks: [
          ["s8a", "Bump price; announce 'final, never again' LTD to the list"],
          ["s8b", "Close it — and actually close it forever"],
        ],
      },
    ],
  },
  {
    phase: "Phase 4 — Reviews → MRR (live-or-die)",
    steps: [
      {
        id: "s9",
        title: "9. Turn LTD buyers into reviews & ambassadors",
        note: "They WANT you to succeed. Reviews boost domain ranking and trust. This is the live-or-die moment — cash is depleting, MRR must take over.",
        tasks: [
          ["s9a", "Ask LTD customers for honest reviews on Trustpilot, G2, Capterra"],
          ["s9b", "Feature testimonials on the site"],
        ],
      },
      {
        id: "s10",
        title: "10. Convert to MRR + answer Reddit relentlessly",
        note: "Shift focus to monthly subscriptions; answer questions where competitors are mentioned. Survive until MRR covers burn.",
        tasks: [
          ["s10a", "Push monthly plans (Starter/Pro/Premium) as the default"],
          ["s10b", "Answer Reddit/forum threads asking for clipping tools (authentic, not spammy)"],
          ["s10c", "Track MRR weekly until it exceeds monthly burn"],
        ],
      },
    ],
  },
];

const ALL_TASK_IDS = PHASES.flatMap((p) => p.steps.flatMap((s) => s.tasks.map((t) => t[0])));

function loadDone() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

// Collapsed by default; remembers whether this admin left it open.
const OPEN_KEY = "admindash.founderPlaybook.open";
function loadOpen() {
  try {
    return localStorage.getItem(OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

export default function FounderPlaybook() {
  const [done, setDone] = useState(loadDone);
  const [open, setOpen] = useState(loadOpen);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {
      /* no-op */
    }
  }, [open]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(done));
    } catch {
      /* no-op */
    }
  }, [done]);

  const completed = useMemo(
    () => ALL_TASK_IDS.filter((id) => done[id]).length,
    [done],
  );
  const pct = Math.round((completed / ALL_TASK_IDS.length) * 100);

  const toggle = (id) => setDone((d) => ({ ...d, [id]: !d[id] }));

  return (
    <div className="fp-card admindash-fade-in">
      <button className="fp-head" onClick={() => setOpen((o) => !o)}>
        <div>
          <h2 className="fp-title"><FiTarget /> Founder Playbook — the build-it-can't-fail framework</h2>
          <div className="fp-sub">
            Mike's 10-step SaaS playbook (Starter Story), adapted to Clipbait + the Lifetime-Deal motion.
          </div>
        </div>
        <div className="fp-progress-wrap">
          <span className="fp-progress-num">{pct}%</span>
          <span className="fp-chevron">{open ? "▲" : "▼"}</span>
        </div>
      </button>

      <div className="fp-progress-track">
        <div className="fp-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="fp-progress-label">
        {completed} / {ALL_TASK_IDS.length} tasks done
      </div>

      {open && (
        <div className="fp-body">
          {/* LTD explainer */}
          <div className="fp-ltd">
            <div className="fp-ltd-title"><FiDollarSign /> How the Lifetime Deal (LTD) works</div>
            <ul>
              <li>
                <b>What "lifetime" means here:</b> a one-time payment → a large{" "}
                <b>never-expiring credit grant</b> (Clipbait already supports never-expiring
                grants — see a user's "credits remaining" breakdown), or a <b>Lifetime plan</b>{" "}
                that drips X credits/month forever.
              </li>
              <li>
                <b>Sequence:</b> private LTD in warm communities (~$30k) → spend it on content →{" "}
                <b>AppSumo</b> (~$100k) → a final "last ever" private LTD (scarcity) → reviews → MRR.
              </li>
              <li>
                <b>Fulfill in Clipbait today:</b> use admin <i>Gift credits</i> on a user
                (never-expiring) for each LTD buyer, or add a "lifetime" plan tier.
              </li>
              <li>
                <b>Pricing idea:</b> $99–$149 one-time. With ~$0.14/clip COGS, even heavy LTD
                users stay profitable — front-loaded cash to fund content + survival.
              </li>
              <li>
                <b>Targets to hit:</b> Private LTD <b>$30k</b> → AppSumo close <b>$100k</b> in the bank.
              </li>
            </ul>
          </div>

          {PHASES.map((phase) => (
            <div key={phase.phase} className="fp-phase">
              <div className="fp-phase-title">{phase.phase}</div>
              {phase.steps.map((step) => {
                const stepDone = step.tasks.every((t) => done[t[0]]);
                return (
                  <div key={step.id} className={`fp-step${stepDone ? " fp-step--done" : ""}`}>
                    <div className="fp-step-title">{step.title}</div>
                    <div className="fp-step-note">{step.note}</div>
                    <div className="fp-tasks">
                      {step.tasks.map(([tid, label]) => (
                        <label key={tid} className={`fp-task${done[tid] ? " fp-task--done" : ""}`}>
                          <input
                            type="checkbox"
                            checked={!!done[tid]}
                            onChange={() => toggle(tid)}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
